"use client";

import * as React from "react";
import { useRouter } from "next/navigation";
import { clientLogger } from "@/lib/client-logger";
const log = clientLogger.child('session');
import { createClient } from "@/utils/supabase/client";
import { 
  initializeSessionManager, 
  cleanupSessionManager,
  getSessionStatus 
} from "@/lib/auth/session-manager";
import { setRememberMe } from "@/lib/auth/session-config";
import { 
  checkBiometricAvailability,
  promptBiometricSetup,
  authenticateWithBiometric,
  clearBiometric 
} from "@/lib/auth/biometric";
import { showSuccess, showError } from "@/lib/utils/toast-helpers";

interface SessionProviderProps {
  children: React.ReactNode;
}

/**
 * Session Provider Component
 * 
 * Wraps the app and manages:
 * - Automatic session refresh
 * - Biometric authentication
 * - Session persistence
 * - Service worker communication
 */
export function SessionProvider({ children }: SessionProviderProps) {
  const router = useRouter();
  const [isInitialized, setIsInitialized] = React.useState(false);
  
  /**
   * Refresh session when service worker requests it
   */
  const refreshSessionFromServiceWorker = React.useCallback(async () => {
    try {
      const supabase = createClient();
      const { error } = await supabase.auth.refreshSession();
      
      if (error) {
        log.error('Refresh from SW failed', { err: error });
      }
    } catch (err) {
      log.error('Refresh error', { err });
    }
  }, []);
  
  /**
   * Handle messages from service worker
   */
  const handleServiceWorkerMessage = React.useCallback((event: MessageEvent) => {
    if (event.data && event.data.type === 'SESSION_REFRESH_NEEDED') {
      void refreshSessionFromServiceWorker();
    }
  }, [refreshSessionFromServiceWorker]);
  
  /**
   * Handle session expiration
   */
  const handleSessionExpired = React.useCallback(() => {
    showError("Your session has expired. Please log in again.");
    router.push('/login');
  }, [router]);
  
  /**
   * Handle session refresh
   */
  const handleSessionRefreshed = React.useCallback((_event: Event) => {
    // Session refreshed successfully
  }, []);
  
  /**
   * Handle successful biometric authentication
   */
  const handleBiometricSuccess = React.useCallback((_event: Event) => {
    showSuccess("Welcome back! Biometric authentication successful.");
  }, []);
  
  /**
   * Check if biometric authentication is available and prompt setup
   */
  const checkAndPromptBiometric = React.useCallback(async () => {
    try {
      // Check if user is logged in
      const supabase = createClient();
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session) return;
      
      // Check biometric availability
      const capability = await checkBiometricAvailability();
      
      if (!capability.available) {
        return;
      }
      
      // Check if already set up (don't prompt every time)
      const hasPrompted = sessionStorage.getItem('biometric_prompt_shown');
      if (hasPrompted) return;
      
      // Mark as prompted
      sessionStorage.setItem('biometric_prompt_shown', 'true');
      
      // In a real implementation, show a modal asking if user wants to enable biometrics
      
    } catch (err) {
      log.error('Error checking biometric', { err });
    }
  }, []);
  
  React.useEffect(() => {
    // Only run on client side
    if (typeof window === 'undefined') return;
    
    // Initialize session manager
    initializeSessionManager();
    setIsInitialized(true);
    
    // Set up service worker messaging
    if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
      // Start session monitoring in service worker
      navigator.serviceWorker.controller.postMessage({
        type: 'START_SESSION_MONITORING'
      });
      
      // Listen for session refresh requests from service worker
      navigator.serviceWorker.addEventListener('message', handleServiceWorkerMessage);
    }
    
    // Listen for custom session events
    window.addEventListener('session-expired', handleSessionExpired);
    window.addEventListener('session-refreshed', handleSessionRefreshed);
    window.addEventListener('biometric-auth-success', handleBiometricSuccess);
    
    // Check if biometric auth is available and prompt if not set up
    void checkAndPromptBiometric();
    
    // Cleanup on unmount
    return () => {
      if (typeof window === 'undefined') return;
      
      cleanupSessionManager();
      window.removeEventListener('session-expired', handleSessionExpired);
      window.removeEventListener('session-refreshed', handleSessionRefreshed);
      window.removeEventListener('biometric-auth-success', handleBiometricSuccess);
      
      if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
        navigator.serviceWorker.removeEventListener('message', handleServiceWorkerMessage);
        navigator.serviceWorker.controller.postMessage({
          type: 'STOP_SESSION_MONITORING'
        });
      }
    };
  }, [handleServiceWorkerMessage, handleSessionExpired, handleSessionRefreshed, handleBiometricSuccess, checkAndPromptBiometric]);
  
  // Don't render children until initialized
  if (!isInitialized) {
    return null;
  }
  
  return <>{children}</>;
}

/**
 * Hook to access session utilities
 */
export function useSession() {
  const router = useRouter();
  
  const enableRememberMe = React.useCallback((enabled: boolean) => {
    setRememberMe(enabled);
  }, []);
  
  const setupBiometric = React.useCallback(async (): Promise<boolean> => {
    try {
      const supabase = createClient();
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session) {
        showError("Please log in first");
        return false;
      }
      
      const success = await promptBiometricSetup(
        session.user.id,
        session.user.email || ''
      );
      
      if (success) {
        showSuccess("Biometric authentication enabled!");
        return true;
      } else {
        showError("Failed to set up biometric authentication");
        return false;
      }
    } catch (error) {
      log.error('Biometric setup error', { err: error });
      showError("An error occurred setting up biometric authentication");
      return false;
    }
  }, []);
  
  const authenticateBiometric = React.useCallback(async (): Promise<boolean> => {
    try {
      const userId = await authenticateWithBiometric();
      
      if (userId) {
        // In a full implementation, you'd exchange this for a session token
        // For now, just return success
        return true;
      }
      
      return false;
    } catch (error) {
      log.error('Biometric auth error', { err: error });
      return false;
    }
  }, []);
  
  const logout = React.useCallback(async () => {
    try {
      const supabase = createClient();
      await supabase.auth.signOut();
      clearBiometric();
      setRememberMe(false);
      router.push('/login');
    } catch (error) {
      log.error('Logout error', { err: error });
    }
  }, [router]);
  
  const checkSessionStatus = React.useCallback(async () => {
    return await getSessionStatus();
  }, []);
  
  return {
    enableRememberMe,
    setupBiometric,
    authenticateBiometric,
    logout,
    checkSessionStatus,
  };
}
